import { useState } from "react";
import { ClipboardPaste, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { submitIngredientList } from "../lib/api";

const TIER_COPY = {
  LOW: {
    badge: "Limited data",
    badgeTone: "bg-amber-100 text-amber-900 border-amber-200",
    headline: "Help us check this product accurately",
    sub:
      "We only matched a handful of ingredients in our database — verdicts may miss flagged actives. Paste the full INCI list from the package and we'll review it.",
  },
  MEDIUM: {
    badge: "Partial data",
    badgeTone: "bg-cream-100 text-ink-soft border-surface-line",
    headline: "Spot a missing ingredient?",
    sub:
      "We have part of the ingredient list. Paste the full INCI list from the package to make this check more accurate for everyone.",
  },
};

export default function IngredientSubmissionCTA({
  productId,
  tier,
  hits,
  total,
}) {
  const copy = TIER_COPY[tier];
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  if (!copy) return null;

  const charCount = text.trim().length;
  const commaCount = (text.match(/,/g) || []).length;
  const valid = charCount >= 30 && commaCount >= 3;

  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true);
    try {
      await submitIngredientList(productId, {
        ingredients_text: text.trim(),
        reporter_email: email.trim() || undefined,
      });
      setDone(true);
      toast.success("Thanks \u2014 we'll review your submission", {
        description: "A reviewer typically gets to new submissions within 48 hours.",
      });
    } catch (e) {
      const msg =
        e?.response?.data?.detail ||
        "We couldn't save that submission just now.";
      toast.error("Submission couldn't be saved", {
        description: typeof msg === "string" ? msg : "Check your connection and try again.",
      });
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <section
        data-testid="ingredient-submission-thanks"
        className="rounded-2xl bg-emerald-50 border border-emerald-200 p-6 sm:p-7 flex items-start gap-4"
      >
        <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0 mt-0.5" />
        <div className="space-y-1">
          <h2 className="text-lg font-bold text-emerald-900">
            Submitted for review
          </h2>
          <p className="text-[14px] text-emerald-800/90 leading-relaxed">
            A reviewer will check your submission. Once approved, the
            ingredient list and the data-confidence badge on this product
            will update automatically.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section
      data-testid="ingredient-submission-cta"
      className="rounded-2xl bg-amber-50/60 border border-amber-200 p-6 sm:p-7 space-y-4"
    >
      <div className="flex items-start gap-3 flex-wrap">
        <span
          className={`inline-flex items-center px-2.5 h-6 rounded-full border text-[11px] uppercase tracking-wider font-bold ${copy.badgeTone}`}
        >
          {copy.badge}
        </span>
        {typeof hits === "number" && typeof total === "number" && total > 0 && (
          <span
            data-testid="ingredient-submission-stats"
            className="text-[11.5px] uppercase tracking-[0.16em] font-semibold text-ink-mute"
          >
            {hits}/{total} ingredients matched
          </span>
        )}
      </div>
      <div className="space-y-1.5">
        <h2 className="text-lg font-bold text-ink">{copy.headline}</h2>
        <p className="text-[14px] text-ink-soft leading-relaxed max-w-prose">
          {copy.sub}
        </p>
      </div>

      {!open && (
        <Button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="ingredient-submission-open"
          variant="outline"
          className="border-amber-300 text-amber-900 hover:bg-amber-100"
        >
          <ClipboardPaste className="w-4 h-4 mr-1.5" />
          Paste full ingredient list
        </Button>
      )}

      {open && (
        <div className="space-y-3">
          <label className="block">
            <span className="block text-[11px] uppercase tracking-[0.18em] font-bold text-ink-mute mb-1.5">
              Ingredients (INCI)
            </span>
            <textarea
              data-testid="ingredient-submission-text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={5}
              placeholder="Aqua, Glycerin, Niacinamide, Sodium Hyaluronate, Phenoxyethanol…"
              className="w-full rounded-xl border border-surface-line bg-white px-3.5 py-3 text-[14px] text-ink placeholder:text-ink-light focus:outline-none focus:ring-4 focus:ring-amber-100 focus:border-amber-300"
            />
            <span
              data-testid="ingredient-submission-helper"
              className={`block mt-1 text-[12px] ${valid ? "text-emerald-700" : "text-ink-mute"}`}
            >
              {valid
                ? "Looks good — ready to submit."
                : `${charCount} chars · ${commaCount} commas (need 30+ chars and 4+ ingredients).`}
            </span>
          </label>

          <label className="block">
            <span className="block text-[11px] uppercase tracking-[0.18em] font-bold text-ink-mute mb-1.5">
              Email (optional)
            </span>
            <input
              data-testid="ingredient-submission-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full rounded-xl border border-surface-line bg-white px-3.5 h-11 text-[14px] text-ink placeholder:text-ink-light focus:outline-none focus:ring-4 focus:ring-amber-100 focus:border-amber-300"
            />
            <span className="block mt-1 text-[12px] text-ink-mute">
              Only used if our reviewer needs to clarify something.
            </span>
          </label>

          <div className="flex flex-wrap items-center gap-3 pt-1">
            <Button
              type="button"
              onClick={submit}
              disabled={!valid || busy}
              data-testid="ingredient-submission-submit"
              className="bg-ink hover:bg-ink-soft text-cream-50"
            >
              {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
              Submit for review
            </Button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="ingredient-submission-cancel"
              className="text-[13px] font-semibold text-ink-mute hover:text-ink"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
